/**
 * Activity Summary Component
 * Provides a high-level overview of current system activity
 */

import React from 'react';
import { 
  ClockIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const ActivitySummary = ({ 
  stats = null,
  reservations = [],
  loading = false,
  showTitle = true,
  customTitle = "Activity Overview",
  className = ""
}) => {
  const getOverdueReservations = () => {
    if (!reservations || !Array.isArray(reservations)) return [];
    
    const now = new Date();
    return reservations.filter(reservation => {
      if (!reservation.startTime || !reservation.reservationDuration) return false;
      const start = new Date(reservation.startTime);
      const expectedReturn = new Date(start.getTime() + (reservation.reservationDuration * 60 * 60 * 1000));
      return now > expectedReturn && reservation.status === 'ACTIVE';
    });
  };

  const calculateTotalRevenue = () => {
    if (!reservations || !Array.isArray(reservations)) return 0;
    
    return reservations.reduce((total, reservation) => {
      return total + (reservation.totalAmount || 0);
    }, 0);
  };

  const overdueReservations = getOverdueReservations();
  const totalRevenue = calculateTotalRevenue();

  const summaryItems = [
    {
      id: 'active-reservations',
      icon: ClockIcon,
      label: 'Active Reservations',
      value: reservations.length || stats?.reservations?.active || 0,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      status: reservations.length > 0 ? 'active' : 'inactive'
    },
    {
      id: 'available-items',
      icon: TruckIcon,
      label: 'Available Items',
      value: stats?.inventory?.statusCounts?.AVAILABLE || 0,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      status: (stats?.inventory?.statusCounts?.AVAILABLE || 0) > 0 ? 'active' : 'inactive'
    },
    {
      id: 'overdue-reservations',
      icon: ExclamationTriangleIcon,
      label: 'Overdue Returns',
      value: overdueReservations.length,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      status: overdueReservations.length > 0 ? 'warning' : 'good'
    },
    {
      id: 'maintenance-due',
      icon: ExclamationTriangleIcon,
      label: 'Maintenance Due',
      value: stats?.inventory?.maintenanceDue || 0,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      status: (stats?.inventory?.maintenanceDue || 0) > 0 ? 'warning' : 'good'
    },
    {
      id: 'total-customers',
      icon: UserGroupIcon,
      label: 'Total Customers',
      value: stats?.customers?.total || 0,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      status: 'info'
    },
    {
      id: 'todays-revenue',
      icon: CurrencyDollarIcon,
      label: 'Today\'s Revenue',
      value: `$${(stats?.payments?.totalAmount || totalRevenue).toFixed(2)}`,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      status: 'info'
    }
  ];

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        {showTitle && (
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">{customTitle}</h2>
          </div>
        )}
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-gray-100 rounded-lg p-4">
                  <div className="h-6 w-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-1"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {showTitle && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">{customTitle}</h2>
        </div>
      )}
      
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {summaryItems.map((item) => {
            const IconComponent = item.icon;
            
            return (
              <div key={item.id} className={`${item.bgColor} rounded-lg p-4 text-center`}>
                <IconComponent className={`h-6 w-6 ${item.color} mx-auto mb-2`} />
                <p className="text-xs text-gray-600 mb-1">{item.label}</p>
                <p className={`text-lg font-semibold ${item.color}`}>
                  {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                </p>
                
                {item.status === 'warning' && item.value > 0 && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Action Needed
                    </span>
                  </div>
                )}
                
                {item.status === 'good' && item.value === 0 && item.id.includes('overdue' || 'maintenance') && (
                  <div className="mt-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 mx-auto" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Key Insights */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {reservations.length === 0 ? (
                <div className="flex items-center text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">All equipment available</span>
                </div>
              ) : (
                <div className="flex items-center text-blue-600">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    {reservations.length} active rental{reservations.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              
              {overdueReservations.length > 0 && (
                <div className="flex items-center text-red-600">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    {overdueReservations.length} overdue return{overdueReservations.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              
              {(stats?.inventory?.maintenanceDue || 0) > 0 && (
                <div className="flex items-center text-orange-600">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    {stats.inventory.maintenanceDue} item{stats.inventory.maintenanceDue !== 1 ? 's' : ''} need maintenance
                  </span>
                </div>
              )}
            </div>
            
            <div className="text-sm text-gray-500">
              Updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivitySummary;